import { useGetAllAttachments } from "@/api/attachment";
import {
  getHomePage,
  listPuckPages,
  type PuckPageListItem,
} from "@/api/cms-puck/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button.tsx";
import { useAuth } from "@/context/auth/AuthContext";
import { cmsMediaLibraryWhere } from "@/lib/cms-puck/media-utils";
import { CmsAdminShell } from "@/pages/cms/advanced/AdminShell";
import { useQuery } from "@tanstack/react-query";
import {
  FileText,
  Home,
  Image as ImageIcon,
  ListTree,
  Palette,
  Pencil,
  Settings,
} from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCmsNew, useTranslationBundleRevision } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase } from "@/lib/cms/cms-new-phrases";

/**
 * « Tableau de bord » de l'interface WordPress-like du CMS avancé
 * (`/cms/advanced/accueil`, première entrée de la sidebar) : vue d'ensemble du
 * site — compteurs (pages publiées / brouillons / médias), dernières pages
 * modifiées (lien direct vers l'éditeur), raccourcis vers les sections.
 * `/cms/advanced` RESTE la liste des Pages (liens existants inchangés).
 */

const PAGES_KEY = ["cms-puck", "pages"] as const;
const HOME_KEY = ["cms-puck", "home"] as const;

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatCard({
  label,
  value,
  onClick,
}: {
  label: string;
  value: number | string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-left shadow-sm transition-shadow hover:shadow-md"
    >
      <p className="text-2xl font-semibold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </button>
  );
}

const SHORTCUTS = [
  {
    path: "/cms/advanced",
    key: "cms.new.auto.label.gerer_les_pages",
    fallback: "Gérer les pages",
    icon: FileText,
  },
  {
    path: "/cms/advanced/menus",
    key: "cms.new.auto.label.menus_de_navigation",
    fallback: "Menus de navigation",
    icon: ListTree,
  },
  {
    path: "/cms/advanced/medias",
    key: "cms.new.auto.label.mediatheque",
    fallback: "Médiathèque",
    icon: ImageIcon,
  },
  {
    path: "/cms/advanced/apparence",
    key: "cms.new.auto.label.apparence_palette",
    fallback: "Apparence & palette",
    icon: Palette,
  },
  {
    path: "/cms/advanced/reglages",
    key: "cms.new.auto.label.reglages_generaux",
    fallback: "Réglages généraux",
    icon: Settings,
  },
] as const;

export default function CMSAdvancedAccueil() {
  const cmsNew = useCmsNew();
  const translationRevision = useTranslationBundleRevision();
  const navigate = useNavigate();
  const { instanceData } = useAuth();
  const cmsId = instanceData?.CMS?.id;
  const shortcuts = useMemo(
    () =>
      SHORTCUTS.map((s) => ({
        path: s.path,
        label: cmsNew(s.key, s.fallback),
        icon: s.icon,
      })),
    [cmsNew, translationRevision],
  );

  const pagesQuery = useQuery({ queryKey: PAGES_KEY, queryFn: () => listPuckPages() });
  const homeQuery = useQuery({ queryKey: HOME_KEY, queryFn: getHomePage });
  const { data: attachments = [] } = useGetAllAttachments({
    where: cmsMediaLibraryWhere(cmsId, instanceData?.id),
    order: { order: "asc" },
  });

  const pages = pagesQuery.data ?? [];
  const homePageId = homeQuery.data?.homePageId ?? null;
  const published = pages.filter((p) => p.status === "PUBLISHED").length;
  const drafts = pages.length - published;
  const recent: PuckPageListItem[] = [...pages]
    .sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    )
    .slice(0, 5);

  return (
    // Pas de bouton « Voir le site » ici : déjà présent dans la sidebar de
    // CmsAdminShell, pas besoin de le dupliquer sur le tableau de bord.
    <CmsAdminShell title={cmsNew("cms.new.auto.title.tableau_de_bord", "Tableau de bord")} subtitle={cmsNew("cms.new.auto.subtitle.vue_densemble_du_site", "Vue d'ensemble du site.")}>
      <div className="flex flex-col gap-6 px-6 py-6">
        {/* Compteurs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard
            label={cmsNew("cms.new.auto.label.pages_publiees", "Pages publiées")}
            value={pagesQuery.isLoading ? "…" : published}
            onClick={() => navigate("/cms/advanced")}
          />
          <StatCard
            label={cmsNew("cms.new.auto.label.brouillons", "Brouillons")}
            value={pagesQuery.isLoading ? "…" : drafts}
            onClick={() => navigate("/cms/advanced")}
          />
          <StatCard
            label={cmsNew("cms.new.auto.label.medias", "Médias")}
            value={attachments.length}
            onClick={() => navigate("/cms/advanced/medias")}
          />
          <StatCard
            label={cmsNew("cms.new.auto.label.page_daccueil", "Page d'accueil")}
            value={homePageId ? cmsPhrase(cmsNew, "homeDefined") : cmsPhrase(cmsNew, "homeToDefine")}
            onClick={() => navigate("/cms/advanced")}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Dernières pages modifiées */}
          <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
            <header className="border-b border-slate-100 px-4 py-3">
              <h2 className="text-sm font-semibold text-slate-900">
                {cmsPhrase(cmsNew, "recentPages")}
              </h2>
            </header>
            {pagesQuery.isLoading ? (
              <p className="px-4 py-8 text-center text-sm text-slate-400">
                {cmsNew("cms.new.auto.text.chargement", "Chargement…")}
              </p>
            ) : recent.length === 0 ? (
              <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
                <p className="text-sm text-slate-500">{cmsNew("cms.new.auto.text.aucune_page_pour_le_moment", "Aucune page pour le moment.")}</p>
                <Button size="sm" onClick={() => navigate("/cms/advanced")}>
                  {cmsPhrase(cmsNew, "createFirstPage")}
                </Button>
              </div>
            ) : (
              <ul className="list-none divide-y divide-slate-100">
                {recent.map((page) => (
                  <li key={page.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/cms/advanced/${page.id}`)}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-slate-50"
                    >
                      <Pencil size={14} className="shrink-0 text-slate-400" />
                      <span className="min-w-0 flex-1 truncate text-sm text-slate-700">
                        {page.name || cmsPhrase(cmsNew, "unnamedPage")}
                      </span>
                      {homePageId === page.id ? (
                        <Badge variant="default" className="gap-1">
                          <Home size={11} /> {cmsPhrase(cmsNew, "homeBadge")}
                        </Badge>
                      ) : null}
                      <Badge
                        variant={
                          page.status === "PUBLISHED" ? "default" : "secondary"
                        }
                      >
                        {page.status === "PUBLISHED"
                          ? cmsPhrase(cmsNew, "published")
                          : cmsPhrase(cmsNew, "draft")}
                      </Badge>
                      <span className="shrink-0 text-xs text-slate-400">
                        {formatDate(page.updatedAt)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Raccourcis */}
          <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
            <header className="border-b border-slate-100 px-4 py-3">
              <h2 className="text-sm font-semibold text-slate-900">{cmsNew("cms.new.auto.text.raccourcis", "Raccourcis")}</h2>
            </header>
            <ul className="list-none divide-y divide-slate-100">
              {shortcuts.map(({ path, label, icon: Icon }) => (
                <li key={path}>
                  <button
                    type="button"
                    onClick={() => navigate(path)}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                  >
                    <Icon size={15} className="shrink-0 text-slate-400" />
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </CmsAdminShell>
  );
}
